import { RecordResponse } from '@/models/ServiceTypes';
import {
  advisorRequiresInvestorId,
  advisorScopeMissingError,
} from '@/utils/advisorScoping';
import { buildRoleAwarePrefix } from '@/utils/api';
import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';

// Define types for API responses
interface CashFlowCategory {
  BankTransactionCategoryId: number;
  IsCustom: boolean;
  ParentId: number;
  Name: string;
  IsEditable: boolean;
  ShowAsGroup: boolean;
}

interface CashFlowCategoriesResponse {
  Records: CashFlowCategory[];
  TotalLength: number;
}

// Define BankCategoryOverride type based on example response
export interface BankCategoryOverride {
  WealthAccountBankTransactionOverrideId: number;
  OriginalBankTransactionCategoryId: number;
  OriginalBankTransactionCategory: string;
  BankTransactionCategoryId: number;
  CategoryName: string;
  OriginalDisplayDescription: string;
  Description: string;
  ComparisonOperator: number;
  ComparisonOperatorDisplay: string;
}

/**
 * API service for cashflow-related endpoints
 */
export const cashFlowApi = createApi({
  reducerPath: 'cashFlowApi',
  baseQuery: waServicesQuery,
  tagTypes: ['CashFlowCategories', 'BankCategoryOverrides'],

  endpoints: (builder) => ({
    // Categories endpoint
    getCashflowCategories: builder.query<
      { categories: CashFlowCategory[]; totalCount: number },
      {
        ExcludedAccounts?: number[];
        ExcludedAccountTypes?: number[];
        ExcludedCategories?: number[];
        startDate?: string;
        endDate?: string;
        hideTransfers?: boolean;
        InvestorId?: number;
      } | void
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        if (advisorRequiresInvestorId(currentRole, args?.InvestorId)) {
          return { error: advisorScopeMissingError() };
        }
        const url = `${buildRoleAwarePrefix(
          currentRole,
          args?.InvestorId,
        )}/BankCategories`;

        const result = await baseQuery({
          url,
          method: 'GET',
          params: args
            ? {
                startDate: args.startDate,
                endDate: args.endDate,
                hideTransfers: args.hideTransfers,
                ...(args.ExcludedAccounts && args.ExcludedAccounts.length
                  ? { ExcludedAccounts: args.ExcludedAccounts.join(',') }
                  : {}),
                ...(args.ExcludedAccountTypes &&
                args.ExcludedAccountTypes.length
                  ? {
                      ExcludedAccountTypes: args.ExcludedAccountTypes.join(','),
                    }
                  : {}),
                ...(args.ExcludedCategories && args.ExcludedCategories.length
                  ? { ExcludedCategories: args.ExcludedCategories.join(',') }
                  : {}),
              }
            : undefined,
        });

        if (result.error) {
          return { error: result.error };
        }

        const data = result.data as CashFlowCategoriesResponse;
        return {
          data: {
            categories: data.Records || [],
            totalCount: data.TotalLength || 0,
          },
        };
      },
      providesTags: ['CashFlowCategories'],
      extraOptions: { maxRetries: 3 },
    }),

    // Bank Category Overrides endpoint
    getBankCategoryOverrides: builder.query<
      RecordResponse<BankCategoryOverride>,
      { InvestorId?: number } | void
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        if (advisorRequiresInvestorId(currentRole, args?.InvestorId)) {
          return { error: advisorScopeMissingError() };
        }

        // Construct the query parameters for sorting
        const params = new URLSearchParams({
          'sort[0].Property': 'OriginalDisplayDescription',
          'sort[0].Direction': 'asc',
          'sort[0].Order': '0',
        });

        const prefix = buildRoleAwarePrefix(currentRole, args?.InvestorId);

        const result = await baseQuery({
          url: `${prefix}/BankCategoryOverrides?${params.toString()}`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        // Assume the response shape matches RecordResponse<BankCategoryOverride>
        const data = result.data as RecordResponse<BankCategoryOverride>;
        return { data };
      },
      providesTags: ['BankCategoryOverrides'],
      extraOptions: { maxRetries: 3 },
    }),

    // Delete Bank Category Override endpoint
    deleteBankCategoryOverride: builder.mutation<
      boolean,
      { overrideId: number; InvestorId?: number }
    >({
      queryFn: async (
        { overrideId, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        if (advisorRequiresInvestorId(currentRole, InvestorId)) {
          return { error: advisorScopeMissingError() };
        }

        const prefix = buildRoleAwarePrefix(currentRole, InvestorId);

        const result = await baseQuery({
          url: `${prefix}/BankCategoryOverrides/${overrideId}`,
          method: 'DELETE',
        });

        // The API returns true on success (200 OK with no content or specific true value?)
        // Assuming success if no error occurs. Adjust if API returns specific boolean.
        if (result.error) {
          console.error('Error deleting bank category override:', result.error);
          return { data: false }; // Indicate failure
        }

        // Assuming a successful delete returns a 2xx status code without specific content.
        // If the API explicitly returns `true`, use `result.data as boolean`.
        return { data: true }; // Indicate success
      },
      // Invalidate the cache for BankCategoryOverrides on successful deletion
      invalidatesTags: ['BankCategoryOverrides'],
      extraOptions: { maxRetries: 0 }, // Typically don't retry deletes
    }),

    // Update Bank Category Override endpoint
    updateBankCategoryOverride: builder.mutation<
      BankCategoryOverride, // Return the updated override on success
      {
        overrideId: number;
        overrideData: Partial<BankCategoryOverride>;
        InvestorId?: number;
      }
    >({
      queryFn: async (
        { overrideId, overrideData, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        if (advisorRequiresInvestorId(currentRole, InvestorId)) {
          return { error: advisorScopeMissingError() };
        }

        const prefix = buildRoleAwarePrefix(currentRole, InvestorId);

        const result = await baseQuery({
          url: `${prefix}/BankCategoryOverrides/${overrideId}`,
          method: 'PUT',
          body: overrideData, // Send the partial update data
        });

        if (result.error) {
          console.error('Error updating bank category override:', result.error);
          // Return the error to be handled by the component
          return { error: result.error as FetchBaseQueryError };
        }

        // Assuming the API returns the updated override object on success
        // If it returns only status 200/true, the caller might need
        // to merge `overrideData` into the cached data manually if needed immediately,
        // but invalidatesTags should trigger a refetch anyway.
        return { data: result.data as BankCategoryOverride }; // Return the updated override
      },
      // Invalidate the cache for BankCategoryOverrides on successful update
      // This will trigger a refetch of the getBankCategoryOverrides query
      invalidatesTags: (result, error, { overrideId }) =>
        result ? [{ type: 'BankCategoryOverrides', id: overrideId }] : [],
      extraOptions: { maxRetries: 1 }, // Allow retries for updates
    }),
  }),
});

// Export the auto-generated hooks for use in components
export const {
  useGetCashflowCategoriesQuery,
  useGetBankCategoryOverridesQuery,
  useDeleteBankCategoryOverrideMutation,
  useUpdateBankCategoryOverrideMutation,
} = cashFlowApi;
